import React, { memo, useCallback, useMemo, useState } from 'react';
import { Chip, emphasize, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ClassNameMap } from '@mui/styles';
import { LabelValueSource } from 'graphql/types';

import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { useChipState } from '../../hooks/useChipState';

type ReadonlyLabelChipProps = {
  className?: string;
  label?: TimesheetLabel | null;
  isChipClickable?: boolean;
  size?: 'medium' | 'small';
  chipRef?: React.MutableRefObject<HTMLDivElement | null>;
  classes?: ClassNameMap;
};

const useChipStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '100%',
    minWidth: '30px'
  },
  clickableColorPrimary: {
    color: theme.palette.timesheetLabel.chipClickableColor,
    backgroundColor: theme.palette.timesheetLabel.nonEmptylabel,
    '&:hover, &:focus': {
      background: emphasize(theme.palette.timesheetLabel.nonEmptylabel, 0.08)
    },
    '&:active': {
      background: emphasize(theme.palette.timesheetLabel.nonEmptylabel, 0.12)
    }
  },
  nonClickableColorPrimary: {
    color: theme.palette.timesheetLabel.chipClickableColor,
    backgroundColor: theme.palette.timesheetLabel.nonEmptylabel
  },
  suggestionOutlinedSecondary: {
    color: theme.palette.timesheetLabel.nonEmptylabel,
    borderColor: theme.palette.timesheetLabel.nonEmptylabel,
    borderStyle: 'dashed'
  },
  outlinedSecondary: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    borderStyle: 'solid'
  }
}));

const useTooltipStyles = makeStyles((theme) => ({
  popper: {
    zIndex: theme.zIndex.tooltip + 2,
    pointerEvents: 'none'
  }
}));

const ReadonlyLabelChipFN: React.FC<ReadonlyLabelChipProps> = ({
  className,
  label,
  isChipClickable = true,
  size = 'medium',
  chipRef,
  classes: overrideClasses
}: ReadonlyLabelChipProps) => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isSuggestion = useMemo(
    () =>
      isSmartSuggestionEnabled &&
      (label?.value?.stringValue || label?.value?.numericValue) &&
      label?.valueSource === LabelValueSource.Suggestion
        ? true
        : false,
    [
      isSmartSuggestionEnabled,
      label?.value?.numericValue,
      label?.value?.stringValue,
      label?.valueSource
    ]
  );
  const classes = useChipStyles({ classes: overrideClasses });
  const chipClasses = useMemo(() => {
    const cls: ClassNameMap = {
      root: classes.root,
      colorPrimary: classes.nonClickableColorPrimary,
      clickableColorPrimary: classes.clickableColorPrimary
    };
    cls['outlinedSecondary'] = isSuggestion
      ? classes.suggestionOutlinedSecondary
      : classes.outlinedSecondary;

    return cls;
  }, [classes, isSuggestion]);
  const tooltipClasses = useTooltipStyles();
  const { isEmptyValue: isEmpty, chipLabel, toolTipText } = useChipState(label);
  const clickableChiptestID = useMemo(
    () => `chip_${label?.name}_${label?.id}`,
    [label]
  );

  const labelCompoennt = useMemo(() => {
    return <Typography variant="caption">{chipLabel}</Typography>;
  }, [chipLabel]);

  const [showTooltip, setShowTooltip] = useState(false);

  const showTooltipFn = useCallback(() => setShowTooltip(true), [
    setShowTooltip
  ]);
  const hideTooltipFn = useCallback(() => setShowTooltip(false), [
    setShowTooltip
  ]);

  const chip = useMemo(
    () => (
      <Chip
        className={className}
        ref={chipRef}
        onMouseOver={showTooltipFn}
        onMouseLeave={hideTooltipFn}
        data-testid={clickableChiptestID}
        label={labelCompoennt}
        variant={!isEmpty && !isSuggestion ? 'filled' : 'outlined'}
        color={!isEmpty && !isSuggestion ? 'primary' : 'secondary'}
        size={size}
        clickable={isChipClickable}
        classes={chipClasses}
      />
    ),
    [
      chipClasses,
      chipRef,
      className,
      clickableChiptestID,
      hideTooltipFn,
      isChipClickable,
      isEmpty,
      isSuggestion,
      labelCompoennt,
      showTooltipFn,
      size
    ]
  );

  return showTooltip ? (
    //tooltip has unwanted re-render during page load it cause around 160ms for 30 notes with average 9 labels
    <Tooltip title={toolTipText} classes={tooltipClasses}>
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};

export const ReadonlyLabelChip = memo(ReadonlyLabelChipFN);
